import { StateError } from '../errors';
import { axialDistance, hexId, ringCells, HEX_DIRS, type HexCoord } from './axial';

// The official disc-laying spiral (outline §3, plan §6.5): counterclockwise inward from a
// corner — ring k ccw from the start corner, then ring k−1 from its cell nearest that
// corner continuing ccw, then inward to the center. Letters A.. are assigned in walk order
// SKIPPING deserts (the skip is what breaks the official red-spacing guarantee — outline
// §3; never test that). Generalizes to any full-hexagon radius.

/** The full spiral walk for a hexagon of `radius`, from ring-corner index `startCorner`. */
export function spiralWalk(radius: number, startCorner: number): HexCoord[] {
  const out: HexCoord[] = [];
  const c = ((startCorner % 6) + 6) % 6;
  for (let k = radius; k >= 1; k--) {
    // each inner ring starts at ITS cell nearest the original start corner and continues
    // ccw — for a corner-aligned start that is exactly the ring's own corner c cell,
    // which ringCells(k, c) begins at (plan §6.5).
    out.push(...ringCells(k, c));
  }
  out.push({ q: 0, r: 0 });
  // sanity: nearest-to-corner start of each ring really is the corner cell (guards the
  // ringCells contract if HEX_DIRS ever changed)
  const corner = HEX_DIRS[c]!;
  const first = out[0]!;
  if (radius > 0 && axialDistance(first, { q: corner.q * radius, r: corner.r * radius }) !== 0) {
    throw new StateError('spiralWalk: ring enumeration lost its corner alignment');
  }
  return out;
}

/**
 * Assign disc numbers along the walk: cells whose tile is in `skipTiles` get null and do
 * NOT consume a letter; every other cell takes the next letter's number in A..Z order of
 * the config map (outline §2 letters).
 */
export function assignSpiralDiscs(
  walk: HexCoord[],
  tileAt: (h: HexCoord) => string,
  skipTiles: ReadonlySet<string>,
  letters: Readonly<Record<string, number>>,
): Map<string, number | null> {
  const alphabet = Object.keys(letters).sort(); // A, B, C, … (object-key order is not trusted)
  const out = new Map<string, number | null>();
  let next = 0;
  for (const cell of walk) {
    if (skipTiles.has(tileAt(cell))) {
      out.set(hexId(cell.q, cell.r), null);
      continue;
    }
    const letter = alphabet[next];
    if (letter === undefined) throw new StateError(`spiral: ran out of letters at cell ${next + 1} of ${walk.length}`);
    out.set(hexId(cell.q, cell.r), letters[letter]!);
    next += 1;
  }
  if (next !== alphabet.length) {
    throw new StateError(`spiral: ${alphabet.length} letters but only ${next} producing cells`);
  }
  return out;
}
